// src/components/ReviewFormModal.jsx
import React, { useState } from 'react';
import {
  FiX,
  FiStar,
  FiUpload,
  FiAlertCircle,
  FiCheck,
  FiMail,
  FiUser,
  FiBriefcase,
} from 'react-icons/fi';

const ReviewFormModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    occupation: '',
    serviceType: '',
    rating: 0,
    message: '',
    image: null,
  });
  const [hoveredRating, setHoveredRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleRatingClick = rating => {
    setFormData({ ...formData, rating });
  };

  const handleImageChange = e => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.name.trim()) {
      setError('Please enter your name');
      setLoading(false);
      return;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    if (formData.rating === 0) {
      setError('Please select a rating');
      setLoading(false);
      return;
    }
    if (!formData.message.trim()) {
      setError('Please enter your review message');
      setLoading(false);
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('occupation', formData.occupation);
      submitData.append('serviceType', formData.serviceType);
      submitData.append('rating', formData.rating);
      submitData.append('message', formData.message);
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      const result = await onSubmit(submitData);

      if (result && result.success) {
        setSuccess('Review submitted successfully!');
        setTimeout(() => {
          onClose();
          setFormData({
            name: '',
            email: '',
            occupation: '',
            serviceType: '',
            rating: 0,
            message: '',
            image: null,
          });
          setImagePreview(null);
          setSuccess('');
        }, 2000);
      } else {
        setError(result?.error || 'Failed to submit review');
      }
    } catch (err) {
      setError(err.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
    >
      <div
        className="relative max-w-lg w-full rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
        style={{
          backgroundColor: 'var(--bg)',
          border: '1px solid var(--border)',
        }}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold" style={{ color: 'var(--text)' }}>
              Write a Review
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            >
              <FiX className="w-5 h-5" style={{ color: 'var(--muted)' }} />
            </button>
          </div>

          {success && (
            <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-green-500/10 text-green-500 border border-green-500/20">
              <FiCheck className="w-4 h-4" />
              <span className="text-sm">{success}</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-red-500/10 text-red-500 border border-red-500/20">
              <FiAlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiUser
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--muted)' }}
                />
                <input
                  type="text"
                  value={formData.name}
                  onChange={e =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="Your full name"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiMail
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--muted)' }}
                />
                <input
                  type="email"
                  value={formData.email}
                  onChange={e =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            {/* Occupation */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                Occupation
              </label>
              <div className="relative">
                <FiBriefcase
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--muted)' }}
                />
                <input
                  type="text"
                  value={formData.occupation}
                  onChange={e =>
                    setFormData({ ...formData, occupation: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="e.g., Creative Director"
                />
              </div>
            </div>

            {/* Service Type */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                Service Type
              </label>
              <select
                value={formData.serviceType}
                onChange={e =>
                  setFormData({ ...formData, serviceType: e.target.value })
                }
                className="w-full px-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="">Select service type</option>
                <option value="Web Development">Web Development</option>
                <option value="UI/UX Design">UI/UX Design</option>
                <option value="Branding">Branding</option>
                <option value="App Development">App Development</option>
                <option value="Ready-Made Package">Ready-Made Package</option>
                <option value="Subscription Plan">Subscription Plan</option>
                <option value="Consulting">Consulting</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Rating */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    onClick={() => handleRatingClick(star)}
                    className="transition-transform hover:scale-110"
                  >
                    <FiStar
                      className={`w-6 h-6 ${
                        star <= (hoveredRating || formData.rating)
                          ? 'fill-current text-yellow-400'
                          : 'text-gray-400'
                      }`}
                    />
                  </button>
                ))}
                <span
                  className="ml-2 text-sm"
                  style={{ color: 'var(--muted)' }}
                >
                  {formData.rating > 0
                    ? `${formData.rating} out of 5`
                    : 'Select rating'}
                </span>
              </div>
            </div>

            {/* Message */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                Your Review <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.message}
                onChange={e =>
                  setFormData({ ...formData, message: e.target.value })
                }
                rows="4"
                className="w-full px-4 py-2 rounded-xl outline-none transition-all focus:ring-1 resize-none"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
                placeholder="Share your experience..."
                required
              />
            </div>

            {/* Image Upload */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                Profile Image (Optional)
              </label>
              <div className="flex items-center gap-4">
                <label
                  className="cursor-pointer px-4 py-2 rounded-xl text-sm transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiUpload className="inline mr-2" />
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
                {imagePreview && (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, image: null });
                        setImagePreview(null);
                      }}
                      className="absolute -top-1 -right-1 p-0.5 rounded-full bg-red-500 text-white"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                Max size: 5MB. Formats: JPG, PNG
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                {loading ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReviewFormModal;
